import React, { createContext, useContext, useCallback, useEffect, useState } from 'react';
import axios from '../api/axios';
import { verifyLoginTwoFactor } from '../api/security';
import { verifyLoginOtp } from '../api/auth';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [pendingTwoFactor, setPendingTwoFactor] = useState(null);

    const checkAuth = async () => {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get('/api/user');
        setUser(response);
        setIsAuthenticated(true);

        const tenantId = response?.tenant?.id || response?.tenants_list?.[0]?.id || response?.domain?.tenant_id;
        const tenantSlug = response?.tenant?.slug || response?.tenants_list?.[0]?.slug || response?.domain?.domain?.split('.')[0];
        if (tenantId) {
          localStorage.setItem('tenant_id', String(tenantId));
        }
        if (tenantSlug) {
          localStorage.setItem('tenant_slug', String(tenantSlug));
        }
      } catch (error) {
        console.error('Auth check failed', error);
        localStorage.removeItem('auth_token');
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    checkAuth();
  }, []);


  const refreshUser = useCallback(async () => {
    await checkAuth();
  }, []);

  const getAuthEndpoint = useCallback(() => {
    const hostname = window.location.hostname;
    const isMobileApp = window.Capacitor || window.location.protocol === 'capacitor:';
    const isCentralDomain = ['localhost', '127.0.0.1', 'the-society.net'].includes(hostname);

    return isMobileApp || isCentralDomain ? '/api/v1/auth/login' : '/api/v1/tenant/auth/login';
  }, []);

  const login = useCallback(async (credentials) => {
    const hostname = window.location.hostname;
    const isMobileApp = window.Capacitor || window.location.protocol === 'capacitor:';
    const isCentralDomain = ['localhost', '127.0.0.1', 'the-society.net'].includes(hostname);
    const authEndpoint = getAuthEndpoint();

    const response = await axios.post(authEndpoint, {
      ...credentials,
      device_name: isMobileApp ? 'mobile_app' : (isCentralDomain ? 'central_web' : 'tenant_web'),
    });

    if (response?.requires_2fa) {
      if (response?.tenant) {
        localStorage.setItem('tenant_id', String(response.tenant.id));
        localStorage.setItem('tenant_slug', String(response.tenant.slug));
      }

      setPendingTwoFactor({
        email: credentials.email,
        challenge_token: response.challenge_token,
        device_name: isMobileApp ? 'mobile_app' : (isCentralDomain ? 'central_web' : 'tenant_web'),
        authEndpoint,
      });

      return response;
    }

    const payload = response?.data || response;
    const authToken = payload?.token;
    const authUser = payload?.user;

    const tenantId = payload?.tenant?.id || authUser?.tenant?.id || authUser?.tenants_list?.[0]?.id || authUser?.domain?.tenant_id;
    const tenantSlug = payload?.tenant?.slug || authUser?.tenant?.slug || authUser?.tenants_list?.[0]?.slug || authUser?.domain?.domain?.split('.')[0];

    if (tenantId) {
      localStorage.setItem('tenant_id', String(tenantId));
    }
    if (tenantSlug) {
      localStorage.setItem('tenant_slug', String(tenantSlug));
    }

    if (authToken) {
      localStorage.setItem('auth_token', authToken);
    }

    setPendingTwoFactor(null);
    setUser(authUser);
    setIsAuthenticated(true);

    return response;
  }, [getAuthEndpoint]);

  const loginWithOtp = useCallback(async (credentials) => {
    const hostname = window.location.hostname;
    const isMobileApp = window.Capacitor || window.location.protocol === 'capacitor:';
    const isCentralDomain = ['localhost', '127.0.0.1', 'the-society.net'].includes(hostname);

    const response = await verifyLoginOtp(credentials.contact, credentials.code, isMobileApp ? 'mobile_app' : (isCentralDomain ? 'central_web' : 'tenant_web'));

    const payload = response?.data || response;
    const authToken = payload?.token;
    const authUser = payload?.user;

    const tenantId = payload?.tenant?.id || authUser?.tenant?.id || authUser?.tenants_list?.[0]?.id || authUser?.domain?.tenant_id;
    const tenantSlug = payload?.tenant?.slug || authUser?.tenant?.slug || authUser?.tenants_list?.[0]?.slug || authUser?.domain?.domain?.split('.')[0];

    if (tenantId) {
      localStorage.setItem('tenant_id', String(tenantId));
    }
    if (tenantSlug) {
      localStorage.setItem('tenant_slug', String(tenantSlug));
    }

    if (authToken) {
      localStorage.setItem('auth_token', authToken);
    }

    setPendingTwoFactor(null);
    setUser(authUser);
    setIsAuthenticated(true);

    return response;
  }, []);

  const verifyTwoFactor = useCallback(async (code) => {
    if (!pendingTwoFactor) {
      throw new Error('No pending two-factor login challenge.');
    }

    const endpoint = pendingTwoFactor.authEndpoint.replace('/login', '/2fa/verify');
    const response = await verifyLoginTwoFactor(endpoint, {
      email: pendingTwoFactor.email,
      challenge_token: pendingTwoFactor.challenge_token,
      code,
      device_name: pendingTwoFactor.device_name,
    });

    const payload = response?.data || response;
    const authToken = payload?.token;
    const authUser = payload?.user;

    const tenantId = payload?.tenant?.id || authUser?.tenant?.id || authUser?.tenants_list?.[0]?.id || authUser?.domain?.tenant_id;
    const tenantSlug = payload?.tenant?.slug || authUser?.tenant?.slug || authUser?.tenants_list?.[0]?.slug || authUser?.domain?.domain?.split('.')[0];

    if (tenantId) {
      localStorage.setItem('tenant_id', String(tenantId));
    }
    if (tenantSlug) {
      localStorage.setItem('tenant_slug', String(tenantSlug));
    }

    if (authToken) {
      localStorage.setItem('auth_token', authToken);
    }

    setPendingTwoFactor(null);
    setUser(authUser);
    setIsAuthenticated(true);

    return response;
  }, [pendingTwoFactor]);

  const logout = useCallback(async (options = {}) => {
    try {
      if (!options.skipApiCall) {
        await axios.post('/api/v1/tenant/auth/logout').catch(() => {});
      }
    } finally {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('tenant_id');
      localStorage.removeItem('tenant_slug');
      delete axios.defaults.headers.common['Authorization'];
      delete axios.defaults.headers.common['X-Tenant-ID'];
      delete axios.defaults.headers.common['X-Tenant-Slug'];
      setUser(null);
      setIsAuthenticated(false);
      setPendingTwoFactor(null);
      setLoading(false);
      if (window.Capacitor && window.Capacitor.isNativePlatform()) {
        // Let React render <LoginScreen /> naturally without hard reload
      } else {
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }
  }, []);

  useEffect(() => {
    const handleAuthExpired = () => {
      logout({ skipApiCall: true });
    };

    window.addEventListener('auth-expired', handleAuthExpired);
    return () => window.removeEventListener('auth-expired', handleAuthExpired);
  }, [logout]);

  const switchTenant = useCallback(async (newTenantId) => {
    try {
      const hostname = window.location.hostname;
      const isMobileApp = window.Capacitor || window.location.protocol === 'capacitor:';
      const isCentralDomain = ['localhost', '127.0.0.1', 'the-society.net'].includes(hostname);

      const endpoint = '/api/v1/tenant/auth/switch-tenant';

      const response = await axios.post(endpoint, {
        new_tenant_id: newTenantId,
        device_name: isMobileApp ? 'mobile_app' : (isCentralDomain ? 'central_web' : 'tenant_web'),
      });

      const payload = response?.data || response;
      const authToken = payload?.token;
      const authUser = payload?.user;

      if (payload?.tenant) {
        localStorage.setItem('tenant_id', payload.tenant.id);
        localStorage.setItem('tenant_slug', payload.tenant.slug);
      }

      if (authToken) {
        localStorage.setItem('auth_token', authToken);
      }

      setUser(authUser);
      
      // We can also reload the window to ensure all state is flushed
      window.location.reload();
      
      return response;
    } catch (error) {
      console.error('Failed to switch tenant', error);
      throw error;
    }
  }, []);

  const clearPendingTwoFactor = useCallback(() => setPendingTwoFactor(null), []);

  return (
    <AuthContext.Provider value={{ user, loading, isAuthenticated, login, loginWithOtp, logout, setUser, pendingTwoFactor, verifyTwoFactor, clearPendingTwoFactor, switchTenant, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
